import { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ArrowPathIcon,
  EyeIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { loadAnalyticsSettings, fetchPageViewStats, fetchSessionStats, fetchTopPages } from '../../../lib/analytics';
import { toast } from 'react-hot-toast';

interface AnalyticsStats {
  totalViews: number;
  totalSessions: number;
  avgPagesPerSession: number;
  mobilePercent: number;
  newVisitorPercent: number;
  bounceRate: number;
}

interface TopPage {
  path: string;
  views: number;
}

export default function MobileAdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [topPages, setTopPages] = useState<TopPage[]>([]);
  const [days, setDays] = useState(30);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => { loadData(); }, [days]);

  const loadData = async () => {
    try {
      setLoading(true);
      const settings = await loadAnalyticsSettings();
      setAnalyticsEnabled(settings?.tracking_enabled || false);

      if (!settings?.tracking_enabled) { setLoading(false); return; }

      const [pageViews, sessions, pages] = await Promise.all([
        fetchPageViewStats(days),
        fetchSessionStats(days),
        fetchTopPages(days, 8),
      ]);

      const totalViews = pageViews?.length || 0;
      const totalSessions = sessions?.length || 0;
      const totalPageCount = sessions?.reduce((s: number, sess: any) => s + (sess.page_count || 1), 0) || 0;
      const mobileSessions = sessions?.filter((s: any) => s.device_type === 'mobile').length || 0;
      const newVisitors = sessions?.filter((s: any) => !s.is_returning).length || 0;
      const singlePageSessions = sessions?.filter((s: any) => (s.page_count || 1) <= 1).length || 0;

      setStats({
        totalViews,
        totalSessions,
        avgPagesPerSession: totalSessions > 0 ? totalPageCount / totalSessions : 0,
        mobilePercent: totalSessions > 0 ? (mobileSessions / totalSessions) * 100 : 0,
        newVisitorPercent: totalSessions > 0 ? (newVisitors / totalSessions) * 100 : 0,
        bounceRate: totalSessions > 0 ? (singlePageSessions / totalSessions) * 100 : 0,
      });

      setTopPages(pages || []);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => { setRefreshing(true); loadData(); };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
    </div>
  );

  if (!analyticsEnabled) return (
    <div className="px-4 py-16 text-center">
      <ChartBarIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
      <p className="text-gray-500 font-medium">Analytics not enabled</p>
      <p className="text-gray-400 text-sm mt-1">Enable analytics in Settings to view data</p>
    </div>
  );

  return (
    <div className="px-4 py-4 space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                days === d ? 'bg-golf-orange text-white border-golf-orange' : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 rounded-xl bg-white border border-gray-200 text-gray-500"
        >
          <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Key metrics */}
      {stats && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-1">
                <EyeIcon className="w-4 h-4 text-blue-500" />
                <p className="text-xs text-gray-500">Page Views</p>
              </div>
              <p className="text-2xl font-bold text-golf-gray">{stats.totalViews.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-1">
                <UserGroupIcon className="w-4 h-4 text-green-500" />
                <p className="text-xs text-gray-500">Sessions</p>
              </div>
              <p className="text-2xl font-bold text-golf-gray">{stats.totalSessions.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-1">
                <ChartBarIcon className="w-4 h-4 text-amber-500" />
                <p className="text-xs text-gray-500">Pages/Session</p>
              </div>
              <p className="text-2xl font-bold text-golf-gray">{stats.avgPagesPerSession.toFixed(1)}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-1">
                <ChartBarIcon className="w-4 h-4 text-red-400" />
                <p className="text-xs text-gray-500">Bounce Rate</p>
              </div>
              <p className="text-2xl font-bold text-golf-gray">{stats.bounceRate.toFixed(0)}%</p>
            </div>
          </div>

          {/* Device breakdown */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <p className="text-sm font-semibold text-golf-gray mb-3">Device Breakdown</p>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <div className="flex items-center gap-1.5">
                    <DevicePhoneMobileIcon className="w-4 h-4" />
                    <span>Mobile</span>
                  </div>
                  <span>{stats.mobilePercent.toFixed(0)}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-golf-orange rounded-full" style={{ width: `${stats.mobilePercent}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <div className="flex items-center gap-1.5">
                    <ComputerDesktopIcon className="w-4 h-4" />
                    <span>Desktop</span>
                  </div>
                  <span>{(100 - stats.mobilePercent).toFixed(0)}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${100 - stats.mobilePercent}%` }} />
                </div>
              </div>
            </div>
            <div className="mt-3 flex gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-400">New Visitors</p>
                <p className="font-semibold text-golf-gray">{stats.newVisitorPercent.toFixed(0)}%</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Returning</p>
                <p className="font-semibold text-golf-gray">{(100 - stats.newVisitorPercent).toFixed(0)}%</p>
              </div>
            </div>
          </div>

          {/* Top Pages */}
          {topPages.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-50">
                <p className="text-sm font-semibold text-golf-gray">Top Pages</p>
              </div>
              <div>
                {topPages.map((page, i) => {
                  const max = topPages[0]?.views || 1;
                  return (
                    <div key={page.path} className={`px-4 py-3 ${i < topPages.length - 1 ? 'border-b border-gray-50' : ''}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-gray-600 truncate flex-1">{page.path || '/'}</p>
                        <p className="text-xs font-semibold text-golf-gray ml-2">{page.views.toLocaleString()}</p>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-golf-orange/60 rounded-full" style={{ width: `${(page.views / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
